import React, { useState } from "react"
import { Box, Button, InternalIcon, Link } from "@carrotfertility/carotene-core"
import { AccessOriginAccountDialog } from "#/components/telemedicine/pelvic-floor-therapy/AccessOriginAccountDialog"
import useGetUserInfoConditionsSubset from "#/services/user/hooks/useGetCurrentUserConditionsSubset"
import { useButtonClickTracking } from "#/services/analytics/useButtonClickTracking"

function TelemedicineButton({
  isDisabled,
  onClick,
  children
}: {
  isDisabled: boolean
  onClick: () => void
  children: React.ReactNode
}): JSX.Element {
  return (
    <Box
      sx={(theme) => ({
        marginBlockStart: theme.spacing(theme.tokens.spacing.xs),
        marginBlockEnd: theme.spacing(theme.tokens.spacing.lg),
        ":last-child": { marginBlockEnd: 0 }
      })}
    >
      <Button onClick={onClick} disabled={isDisabled}>
        {children}
      </Button>
    </Box>
  )
}

function TelemedicineLink({
  isDisabled,
  onClick,
  children
}: {
  isDisabled: boolean
  onClick: () => void
  children: React.ReactNode
}): JSX.Element {
  if (isDisabled) {
    return null
  }
  return (
    <Box>
      <Link component="button" endIcon={<InternalIcon fontSize="small" color="primary" />} onClick={onClick}>
        {children}
      </Link>
    </Box>
  )
}

function TelemedicineForPelvicFloorTherapyInternal({
  variant,
  children
}: {
  variant: "button" | "link"
  children: React.ReactNode
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const { data } = useGetUserInfoConditionsSubset()
  const { trackButtonClick } = useButtonClickTracking()
  const isDisabled = data?.hasCarrotPlanOnTheWay ?? false

  const handleClick = () => {
    trackButtonClick({ name: "Access Origin account" })
    setOpen(true)
  }

  return (
    <>
      {variant === "button" ? (
        <TelemedicineButton isDisabled={isDisabled} onClick={handleClick}>
          {children}
        </TelemedicineButton>
      ) : (
        <TelemedicineLink isDisabled={isDisabled} onClick={handleClick}>
          {children}
        </TelemedicineLink>
      )}
      <AccessOriginAccountDialog open={open} onClose={() => setOpen(false)} />
    </>
  )
}

export function TelemedicineForPelvicFloorTherapyButton({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <TelemedicineForPelvicFloorTherapyInternal variant="button">{children}</TelemedicineForPelvicFloorTherapyInternal>
  )
}

export function TelemedicineForPelvicFloorTherapyLink({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <TelemedicineForPelvicFloorTherapyInternal variant="link">{children}</TelemedicineForPelvicFloorTherapyInternal>
  )
}
